import { useMemo, useState, useEffect } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  Avatar,
  AvatarFallback,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { UserPlus, UserX } from "lucide-react";
import { SubscriptionCategory } from "@/models/subscriptions/Subscription";
import { getInvalidLabel } from "@/config/datasources";
import useGroups from "@/hooks/useGroups";
import { useItemService } from "@/hooks/useServices";
import { PATH_KEYS } from "@/services/appServices";
import type { Group } from "@/models/feature/feature";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";

export type LegislationMetadataPreview = {
  datasource: string;
  legislationExternalId?: string | null;
  publishedOn?: string | null;
  isCurrent?: boolean | null;
  documentTypeId?: string | number | null;
};

type ManageGroupSubscriptionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant: "legislation" | "default";
  entityPath: string;
  entityName: string;
  source: string;
  subscribeCategory: SubscriptionCategory;
  /** Legislation only */
  legislationExternalId?: string;
  legislationDatasource?: string;
  legislationMetadata?: LegislationMetadataPreview;
};

type SubscribedGroup = {
  group: Group;
  subscriptionId?: string;
};

// Two-pane picker with a fixed height; the width comes from `size="xl"`.
const DIALOG_CONTENT_CLASS = "h-[75vh] flex flex-col px-0";
const CARD_STYLE =
  "flex flex-col shadow-[0px_2px_6px_1.5px_rgba(0,0,0,0.1)] rounded-lg my-2 gap-2 h-full overflow-hidden";
const CARD_HEADER =
  "flex flex-row justify-between items-center relative p-2 border-b";

const addGroupFormSchema = z.object({
  name: z.string(),
  timelineUpdates: z.boolean(),
  latest: z.boolean(),
});

type AddGroupFormValues = z.infer<typeof addGroupFormSchema>;

const ManageGroupSubscriptionModal = ({
  open,
  onOpenChange,
  variant,
  entityPath,
  entityName,
  source,
  subscribeCategory,
  legislationExternalId,
  legislationDatasource,
  legislationMetadata,
}: ManageGroupSubscriptionModalProps) => {
  const {
    useGetGroups,
    addGroupLegislationSubscription,
    removeGroupLegislationSubscription,
    addGroupSubscription,
    removeGroupSubscription,
  } = useGroups();

  // Every row's group bell mounts this modal closed. The tenant-wide list needs ReadGroups, which
  // loda-moderator lacks although it sees the bell, so fetching while closed 403'd on every hover.
  const { data: groups = [], isLoading: groupsLoading } = useGetGroups({
    enabled: open,
  });

  const legislationSubscriptionQueries =
    useQueriesForGroupLegislationSubscriptions(
      groups,
      open && variant === "legislation"
    );
  const genericSubscriptionQueries = useQueriesForGroupSubscriptions(
    groups,
    open && variant === "default"
  );

  const { subscribedGroups, availableGroups } = useMemo(() => {
    if (variant === "legislation") {
      const subscribed: SubscribedGroup[] = [];
      groups.forEach((group, i) => {
        const res = legislationSubscriptionQueries[i]?.data;
        if (!res) return;
        const match = res.find(
          (s) =>
            s.externalId === legislationExternalId &&
            s.datasource === legislationDatasource
        );
        if (match) subscribed.push({ group, subscriptionId: match.id });
      });
      const subscribedIds = new Set(subscribed.map((s) => s.group.id));
      const available = groups.filter((g) => !subscribedIds.has(g.id));
      return { subscribedGroups: subscribed, availableGroups: available };
    }
    const subscribed: SubscribedGroup[] = [];
    groups.forEach((group, i) => {
      const res = genericSubscriptionQueries[i]?.data;
      if (!res) return;
      const hasEntity = res.some((s) => s.entityPath === entityPath);
      if (hasEntity) subscribed.push({ group });
    });
    const subscribedIds = new Set(subscribed.map((s) => s.group.id));
    const available = groups.filter((g) => !subscribedIds.has(g.id));
    return { subscribedGroups: subscribed, availableGroups: available };
  }, [
    variant,
    groups,
    legislationSubscriptionQueries,
    genericSubscriptionQueries,
    legislationExternalId,
    legislationDatasource,
    entityPath,
  ]);

  const [searchAvailable, setSearchAvailable] = useState("");
  const [searchSubscribed, setSearchSubscribed] = useState("");
  const [addLegislationDialogGroup, setAddLegislationDialogGroup] =
    useState<Group | null>(null);
  const [removingGroupId, setRemovingGroupId] = useState<string | null>(null);
  const [addingGroupId, setAddingGroupId] = useState<string | null>(null);

  const addGroupForm = useForm<AddGroupFormValues>({
    resolver: zodResolver(addGroupFormSchema),
    defaultValues: { name: entityName, timelineUpdates: false, latest: false },
  });

  // The "Abonner gruppe på kilde" form starts clean for every group it is opened for.
  useEffect(() => {
    if (addLegislationDialogGroup) {
      addGroupForm.reset({
        name: entityName,
        timelineUpdates: false,
        latest: false,
      });
    }
  }, [addLegislationDialogGroup, entityName, addGroupForm]);

  const availableFiltered = useMemo(() => {
    if (!searchAvailable.trim()) return availableGroups;
    const q = searchAvailable.trim().toLowerCase();
    return availableGroups.filter((g) => g.name.toLowerCase().includes(q));
  }, [availableGroups, searchAvailable]);
  const subscribedFiltered = useMemo(() => {
    if (!searchSubscribed.trim()) return subscribedGroups;
    const q = searchSubscribed.trim().toLowerCase();
    return subscribedGroups.filter((s) =>
      s.group.name.toLowerCase().includes(q)
    );
  }, [subscribedGroups, searchSubscribed]);

  const handleAddGroup = (group: Group) => {
    if (variant === "legislation") {
      setAddLegislationDialogGroup(group);
      return;
    }
    setAddingGroupId(group.id);
    addGroupSubscription
      .mutateAsync({
        groupId: group.id,
        body: {
          subscribeType: subscribeCategory,
          source,
          entityPath,
          entityName,
        },
      })
      .then(() => showSuccessToast("Gruppen er abonneret."))
      .catch((err) => {
        logger.error("Failed to add group subscription", {
          operation: "addGroupSubscription",
          error: err,
          groupId: group.id,
          entityPath,
          subscribeCategory,
        });
        showErrorToast("Kunne ikke tilføje gruppeabonnement.");
      })
      .finally(() => setAddingGroupId(null));
  };

  const handleAddLegislationConfirm = addGroupForm.handleSubmit(
    async (values) => {
      if (
        !addLegislationDialogGroup ||
        legislationExternalId == null ||
        legislationDatasource == null
      )
        return;
      try {
        await addGroupLegislationSubscription.mutateAsync({
          groupId: addLegislationDialogGroup.id,
          body: {
            externalId: legislationExternalId,
            datasource: legislationDatasource,
            name: values.name.trim() || entityName,
            timelineUpdates: values.timelineUpdates,
            latestExternalId: values.latest ? legislationExternalId : undefined,
          },
        });
        showSuccessToast("Gruppen er abonneret på kilden.");
        setAddLegislationDialogGroup(null);
      } catch (err) {
        logger.error("Failed to add group legislation subscription", {
          operation: "addGroupLegislationSubscription",
          error: err,
          groupId: addLegislationDialogGroup.id,
          legislationExternalId,
          legislationDatasource,
        });
        showErrorToast("Kunne ikke tilføje gruppeabonnement.");
      }
    }
  );

  const handleRemoveGroup = (item: SubscribedGroup) => {
    if (variant === "legislation" && item.subscriptionId) {
      setRemovingGroupId(item.group.id);
      removeGroupLegislationSubscription
        .mutateAsync({
          groupId: item.group.id,
          body: { subscriptionId: item.subscriptionId },
        })
        .then(() => showSuccessToast("Gruppen er fjernet fra abonnementet."))
        .catch((err) => {
          logger.error("Failed to remove group legislation subscription", {
            operation: "removeGroupLegislationSubscription",
            error: err,
            groupId: item.group.id,
            subscriptionId: item.subscriptionId,
          });
          showErrorToast("Kunne ikke fjerne gruppeabonnement.");
        })
        .finally(() => setRemovingGroupId(null));
      return;
    }
    setRemovingGroupId(item.group.id);
    removeGroupSubscription
      .mutateAsync({
        groupId: item.group.id,
        body: {
          subscribeType: subscribeCategory,
          source,
          entityPath,
          entityName,
        },
      })
      .then(() => showSuccessToast("Gruppen er fjernet fra abonnementet."))
      .catch((err) => {
        logger.error("Failed to remove group subscription", {
          operation: "removeGroupSubscription",
          error: err,
          groupId: item.group.id,
          entityPath,
          subscribeCategory,
        });
        showErrorToast("Kunne ikke fjerne gruppeabonnement.");
      })
      .finally(() => setRemovingGroupId(null));
  };

  const isLoading =
    groupsLoading ||
    (variant === "legislation"
      ? legislationSubscriptionQueries.some((q) => q.isLoading)
      : genericSubscriptionQueries.some((q) => q.isLoading));

  useEffect(() => {
    if (!open) {
      setSearchAvailable("");
      setSearchSubscribed("");
      setAddLegislationDialogGroup(null);
    }
  }, [open]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="xl" className={DIALOG_CONTENT_CLASS}>
          <DialogHeader className="px-6">
            <DialogTitle>
              {variant === "legislation"
                ? "Administrer gruppeabonnement på kilde"
                : "Administrer gruppeabonnement"}
            </DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">{entityName}</p>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-hidden flex flex-col px-6">
            {isLoading ? (
              <div className="flex flex-1 items-center justify-center">
                <Spinner className="size-8" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 flex-1 min-h-0 overflow-hidden gap-4">
                <div className="flex flex-col h-full overflow-hidden">
                  <div className={cn(CARD_STYLE)}>
                    <div className={CARD_HEADER}>
                      <p className="text-sm pl-2 flex-1">
                        Tilgængelige grupper
                      </p>
                      <Input
                        type="text"
                        role="searchbox"
                        placeholder="Søg"
                        className="w-1/2 h-8 pr-8"
                        value={searchAvailable}
                        onChange={(e) => setSearchAvailable(e.target.value)}
                      />
                    </div>
                    <div className="flex-1 min-h-0 overflow-auto pl-4 pr-1 py-2">
                      {availableFiltered.length === 0 ? (
                        <p className="text-muted-foreground text-sm flex justify-center items-center py-8">
                          Ingen grupper at tilføje
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 gap-y-2">
                          {availableFiltered.map((group) => (
                            <div
                              key={group.id}
                              className="flex flex-row items-center gap-2 text-sm"
                            >
                              <Avatar className="size-7 shrink-0">
                                <AvatarFallback className="size-7 text-xs">
                                  {group.name?.charAt(0) ?? "?"}
                                </AvatarFallback>
                              </Avatar>
                              <span className="truncate flex-1">
                                {group.name}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-2 shrink-0 opacity-50 hover:opacity-100"
                                disabled={addingGroupId === group.id}
                                onClick={() => handleAddGroup(group)}
                              >
                                {addingGroupId === group.id ? (
                                  <Spinner className="size-4" />
                                ) : (
                                  <UserPlus className="size-4" />
                                )}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col h-full overflow-hidden">
                  <div className={cn(CARD_STYLE)}>
                    <div className={CARD_HEADER}>
                      <p className="text-sm pl-2 flex-1">Abonnerede grupper</p>
                      <Input
                        type="text"
                        role="searchbox"
                        placeholder="Søg"
                        className="w-1/2 h-8 pr-8"
                        value={searchSubscribed}
                        onChange={(e) => setSearchSubscribed(e.target.value)}
                      />
                    </div>
                    <div className="flex-1 min-h-0 overflow-auto pl-4 pr-1 py-2">
                      {subscribedFiltered.length === 0 ? (
                        <p className="text-muted-foreground text-sm flex justify-center items-center py-8">
                          Ingen grupper abonneret
                        </p>
                      ) : (
                        <div className="grid grid-cols-1 gap-y-2">
                          {subscribedFiltered.map((item) => (
                            <div
                              key={item.group.id}
                              className="flex flex-row items-center gap-2 text-sm"
                            >
                              <Avatar className="size-7 shrink-0">
                                <AvatarFallback className="size-7 text-xs">
                                  {item.group.name?.charAt(0) ?? "?"}
                                </AvatarFallback>
                              </Avatar>
                              <span className="truncate flex-1">
                                {item.group.name}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-2 shrink-0 opacity-50 hover:opacity-100"
                                disabled={removingGroupId === item.group.id}
                                onClick={() => handleRemoveGroup(item)}
                              >
                                {removingGroupId === item.group.id ? (
                                  <Spinner className="size-4" />
                                ) : (
                                  <UserX className="size-4" />
                                )}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {variant === "legislation" &&
        addLegislationDialogGroup &&
        legislationExternalId != null &&
        legislationDatasource != null && (
          <FormDialog
            open={true}
            onOpenChange={(next) => {
              if (!next) setAddLegislationDialogGroup(null);
            }}
            isDirty={addGroupForm.formState.isDirty}
            className="sm:max-w-md"
          >
            <Form {...addGroupForm}>
              <form onSubmit={handleAddLegislationConfirm}>
                <DialogHeader>
                  <DialogTitle>Abonner gruppe på kilde</DialogTitle>
                  <p className="text-sm text-muted-foreground">
                    Gruppe:{" "}
                    <span className="font-medium text-foreground">
                      {addLegislationDialogGroup.name}
                    </span>
                  </p>
                </DialogHeader>
                <div className="flex flex-col gap-4 py-4">
                  {legislationMetadata && (
                    <div className="rounded-md border bg-muted/50 p-3 text-sm">
                      <p className="mb-2 font-medium text-muted-foreground">
                        Kildeinformation
                      </p>
                      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                        <dt className="text-muted-foreground">Titel:</dt>
                        <dd className="font-medium">{entityName || "–"}</dd>
                        <dt className="text-muted-foreground">Kilde:</dt>
                        <dd>
                          {legislationMetadata.datasource
                            ? legislationMetadata.datasource
                                .charAt(0)
                                .toUpperCase() +
                              legislationMetadata.datasource.slice(1)
                            : "–"}
                        </dd>
                        <dt className="text-muted-foreground">Nummer:</dt>
                        <dd>
                          {legislationMetadata.legislationExternalId || "–"}
                        </dd>
                        <dt className="text-muted-foreground">Status:</dt>
                        <dd>
                          {legislationMetadata.isCurrent === null ||
                          legislationMetadata.isCurrent === undefined
                            ? "–"
                            : legislationMetadata.isCurrent
                              ? "Gældende"
                              : getInvalidLabel(legislationMetadata.datasource)}
                        </dd>
                      </dl>
                    </div>
                  )}
                  <FormField
                    control={addGroupForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="grid gap-2">
                        <FormLabel>Angiv navn på kildeabonnement</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            id="modal-group-subscribe-name"
                            placeholder={entityName}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {isLovOrLbk(legislationMetadata) && (
                    <>
                      <div className="flex items-center space-x-2">
                        <FormField
                          control={addGroupForm.control}
                          name="timelineUpdates"
                          render={({ field }) => (
                            <Checkbox
                              id="modal-group-subscribe-timeline"
                              checked={field.value}
                              onCheckedChange={(c) =>
                                field.onChange(c === true)
                              }
                            />
                          )}
                        />
                        <Label
                          htmlFor="modal-group-subscribe-timeline"
                          className="cursor-pointer text-sm font-normal"
                        >
                          Inkluder tidslinjeopdateringer
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <FormField
                          control={addGroupForm.control}
                          name="latest"
                          render={({ field }) => (
                            <Checkbox
                              id="modal-group-subscribe-latest"
                              checked={field.value}
                              onCheckedChange={(c) =>
                                field.onChange(c === true)
                              }
                            />
                          )}
                        />
                        <Label
                          htmlFor="modal-group-subscribe-latest"
                          className="cursor-pointer text-sm font-normal"
                        >
                          Abonner på seneste version
                        </Label>
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter>
                  <FormDialogCancel />
                  <Button
                    type="submit"
                    disabled={addGroupLegislationSubscription.isPending}
                  >
                    {addGroupLegislationSubscription.isPending ? (
                      <Spinner className="size-4" />
                    ) : (
                      "Abonner gruppe"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </FormDialog>
        )}
    </>
  );
};

function isLovOrLbk(
  meta: LegislationMetadataPreview | null | undefined
): boolean {
  const id = meta?.documentTypeId;
  if (id == null) return false;
  const num = Number(id);
  return num === 10 || num === 30;
}

function useQueriesForGroupLegislationSubscriptions(
  groups: Group[],
  enabled: boolean
) {
  const groupService = useItemService((s) => s.group);
  return useQueries({
    queries: groups.map((g) => ({
      queryKey: [PATH_KEYS.group, "legislation-subscriptions", g.id],
      queryFn: () =>
        groupService
          .getGroupLegislationSubscriptions(g.id)
          .then((r) => (r.success ? r.value : [])),
      enabled: enabled && !!g.id,
    })),
  });
}

function useQueriesForGroupSubscriptions(groups: Group[], enabled: boolean) {
  const groupService = useItemService((s) => s.group);
  return useQueries({
    queries: groups.map((g) => ({
      queryKey: [PATH_KEYS.group, "subscriptions", g.id],
      queryFn: () =>
        groupService
          .getGroupSubscriptions(g.id, 1)
          .then((r) => (r.success && r.value?.data ? r.value.data : [])),
      enabled: enabled && !!g.id,
    })),
  });
}

export default ManageGroupSubscriptionModal;
